import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
    Modal, 
    Upload, 
    Button, 
    Steps, 
    Table, 
    message, 
    Alert, 
    Radio, 
    Select, 
    Space, 
    Typography,
    Row,
    Col,
    Card,
    Input,
    Tag,
    Tooltip,
    Popconfirm
} from 'antd';
import { InboxOutlined, ExclamationCircleOutlined, PicCenterOutlined, LeftOutlined, RightOutlined, LinkOutlined, InfoCircleOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons';
import apiCore from '../../utils/api-core';
import session from '../../utils/session';
import { createGroupV3 } from '../../reducers/group/resources';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { parseFullName } from 'parse-full-name';
import _ from 'lodash';
import { phone } from 'phone';
import PhoneDisplay, { COUNTRY_FORMATS } from '../common/PhoneDisplay';

const { Dragger } = Upload;
const { Step } = Steps;
const { Text } = Typography;
const { Option } = Select;

const TARGET_FIELDS = [
    { key: 'mobile', label: 'Mobile Phone', required: true, description: 'Required' },
    { key: 'firstname', label: 'First Name', required: true, description: 'Required (or map Full Name)' },
    { key: 'lastname', label: 'Last Name', required: true, description: 'Required' },
    { key: 'fullname', label: 'Full Name', required: false, description: 'Optional (Splits into First/Last)' },
    { key: 'email', label: 'Email', required: false, description: 'Optional' },
    { key: 'tags', label: 'Tags', required: false, description: 'Optional (comma separated)' },
    { key: 'groups', label: 'Groups', required: false, description: 'Optional (comma separated)' }
];

const ImportContactsModal = ({
    visible,
    onCancel,
    onFinish,
    mode = 'execute',
    availableGroups = [],
    targetGroupId,
    requireGroup = false
}) => {
    // ... state ...
    const [currentStep, setCurrentStep] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [executing, setExecuting] = useState(false);
    
    // Step 0: Upload
    const [csvData, setCsvData] = useState([]);
    const [headers, setHeaders] = useState([]);
    
    // Step 1: Map
    // Fixed mapping for core fields
    const [fixedMapping, setFixedMapping] = useState({}); 
    // Dynamic mapping for optional fields [{ id, target: '', column: '' }]
    const [customMappings, setCustomMappings] = useState([]);

    // Step 2-3: Process
    const [processId, setProcessId] = useState(null);
    const [mappedContacts, setMappedContacts] = useState([]);
    const [validationSummary, setValidationSummary] = useState(null);
    
    // Execution options
    const [importStrategy, setImportStrategy] = useState('skip');
    const [selectedGroupId, setSelectedGroupId] = useState(targetGroupId || null);
    const [newGroupName, setNewGroupName] = useState('');
    const [groupSearchText, setGroupSearchText] = useState('');
    const [searchText, setSearchText] = useState('');
    const [reviewFilter, setReviewFilter] = useState('all'); // all, valid, duplicate, invalid
    const [defaultCountry, setDefaultCountry] = useState('US'); // Default to USA as requested
    
    const hasNakedNumbers = useMemo(() => {
        const col = fixedMapping['mobile'];
        if (!col || csvData.length === 0) return false;
        return csvData.some(row => {
            const val = String(row[col] || '').trim();
            return val && !val.startsWith('+');
        });
    }, [fixedMapping, csvData]);

    const resetState = () => {
        setCurrentStep(0);
        setCsvData([]);
        setHeaders([]);
        setFixedMapping({});
        setCustomMappings([]);
        setProcessId(null);
        setMappedContacts([]);
        setValidationSummary(null);
        setImportStrategy(targetGroupId ? 'update' : 'skip');
        setSelectedGroupId(targetGroupId || null);
        setNewGroupName('');
        setGroupSearchText('');
        setUploading(false);
        setValidating(false);
        setExecuting(false);
        setDefaultCountry('US');
    };

    const handleCancel = () => {
        resetState();
        onCancel();
    };

    const processParsedData = (data, metaFields) => {
        setHeaders(metaFields);
        setCsvData(data);
        
        const lowerHeaders = metaFields.map(h => ({ original: h, lower: h.toLowerCase().replace(/[^a-z]/g, '') }));
        
        // 1. Auto-map Fixed Fields
        const newFixed = {};
        const coreFields = ['firstname', 'lastname', 'fullname', 'mobile', 'email'];
        coreFields.forEach(key => {
             const label = key === 'mobile' ? 'mobile' : key; 
             const match = lowerHeaders.find(h => h.lower.includes(label) || h.lower === key);
             if (match) newFixed[key] = match.original;
        });
        setFixedMapping(newFixed);

        // 2. Auto-detect Optional Fields (Tags, Groups)
        const newCustom = [];
        ['tags', 'groups'].forEach(key => {
            const match = lowerHeaders.find(h => h.lower === key || h.lower.includes(key));
            if (match) {
                newCustom.push({ 
                    id: Date.now() + Math.random(), 
                    target: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize
                    column: match.original 
                });
            }
        });
        setCustomMappings(newCustom);

        setCurrentStep(1);
    };

    // Step 0: Parse File
    const handleUpload = (file) => {
        setUploading(true);

        const fileExt = file.name.split('.').pop().toLowerCase();

        if (['xls', 'xlsx'].includes(fileExt)) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const results = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
                    
                    setUploading(false);
                    if (!results || results.length === 0) {
                         message.error('Excel file is empty or could not be parsed');
                         return;
                    }
                    
                    const metaFields = Object.keys(results[0]);
                    processParsedData(results, metaFields);
                } catch (error) {
                    setUploading(false);
                    message.error('Excel Parse Error: ' + error.message);
                }
            };
            reader.readAsArrayBuffer(file);
            return false;
        }

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                setUploading(false);
                if (results.data.length === 0) {
                    message.error('File contains no data rows');
                    return;
                }
                if (results.errors.length > 0) {
                    message.error('Failed to parse CSV/TSV');
                    return;
                }

                processParsedData(results.data, results.meta.fields || []);
            },
            error: (err) => {
                setUploading(false);
                message.error('CSV/TSV Parse Error: ' + err.message);
            }
        });
        return false; 
    };

    const addCustomRow = () => {
        setCustomMappings([...customMappings, { id: Date.now(), target: '', column: '' }]);
    };

    const removeCustomRow = (id) => {
        setCustomMappings(customMappings.filter(m => m.id !== id));
    };

    // Step 1: Process Mapping & Validate
    const handleMapNext = async () => {
        // Validation: Fixed Fields
        if (!fixedMapping['mobile']) {
            message.error('Mobile Phone field is required');
            return;
        }

        const hasFullName = !!fixedMapping['fullname'];
        const hasFirstLast = !!fixedMapping['firstname'] && !!fixedMapping['lastname'];
        
        // If Full Name is NOT mapped, require First AND Last
        if (!hasFullName && !hasFirstLast) {
             message.error('Please map "Full Name" OR both "First Name" and "Last Name"');
             return;
        }
        
        // Validation: Custom Fields
        for (const m of customMappings) {
            if (!m.target || !m.column) {
                message.error('Please complete all custom field mappings or remove empty rows');
                return;
            }
        }

        // Transform Data
        const normalized = csvData.map(row => {
            let firstname = fixedMapping['firstname'] ? row[fixedMapping['firstname']] : '';
            let lastname = fixedMapping['lastname'] ? row[fixedMapping['lastname']] : '';
            let email = fixedMapping['email'] ? row[fixedMapping['email']] : '';
            let mobile = fixedMapping['mobile'] ? row[fixedMapping['mobile']] : '';

            if (mobile) {
                let phoneStr = String(mobile).trim();
                const hasPlus = phoneStr.startsWith('+');
                
                // If it lacks '+', use defaultCountry as a hint
                const phoneResult = phone(phoneStr, { country: hasPlus ? undefined : defaultCountry });
                
                if (phoneResult.isValid) {
                    mobile = phoneResult.phoneNumber;
                } else if (!hasPlus) {
                    // Fallback to trying with '+' if the country hint didn't work and it's missing '+'
                    const secondAttempt = phone('+' + phoneStr, { country: defaultCountry });
                    if (secondAttempt.isValid) {
                        mobile = secondAttempt.phoneNumber;
                    }
                }
            }
            
            // Handle Full Name Priority — always overrides firstname/lastname
            if (fixedMapping['fullname']) {
                 const fullName = row[fixedMapping['fullname']];
                 if (fullName) {
                     const parsed = parseFullName(fullName);
                     firstname = parsed.first || '';
                     lastname = [parsed.middle, parsed.last].filter(Boolean).join(' ') || '';
                 }
            }
            
            // Handle Custom Fields (Tags, Groups, Others)
            let tags = [];
            let groups = [];
            let customFields = {};
            
            customMappings.forEach(m => {
                const val = row[m.column];
                if (!val) return;

                const targetLower = m.target.toLowerCase().trim();
                
                if (targetLower === 'tags' || targetLower === 'tag') {
                    tags.push(...val.split(',').map(t => t.trim()));
                } else if (targetLower === 'groups' || targetLower === 'group') {
                    groups.push(...val.split(',').map(g => g.trim()));
                } else if (targetLower === 'email') {
                    email = val.trim();
                } else {
                    customFields[m.target] = val.trim();
                    tags.push(`${m.target}: ${val.trim()}`);
                }
            });

            return {
                ...customFields,
                firstname: firstname ? firstname.trim() : '',
                lastname: lastname ? lastname.trim() : '',
                mobile: mobile ? mobile.trim() : '',
                email: email ? email.trim() : '',
                tags: tags.filter(Boolean),
                groups: groups.filter(Boolean)
            };
        });

        // Backend Validate
        try {
            setValidating(true);
            const res = await apiCore.post('/api/v3/contacts/import/validate', {
                contacts: normalized
            });
            
            const data = res.data || res; // Handle both wrapped and unwrapped

            setValidationSummary(data.summary);
            setProcessId(data.processId); 
            
            if (data.contacts) {
                // Ensure all valid ones have the formatted number returned by backend if any,
                // but our normalization already tried its best.
                setMappedContacts(data.contacts);
            }

            setCurrentStep(2);
        } catch (e) {
            console.error(e);
            message.error('Validation failed: ' + (e.response?.data?.message || e.message));
        } finally {
            setValidating(false);
        }
    };

    const handleExecute = async () => {
        setExecuting(true);
        try {
            if (mode === 'execute') {
                let groupId = selectedGroupId;

                if (requireGroup && !groupId && !newGroupName) {
                    message.error('Please select or create a group');
                    setExecuting(false);
                    return;
                }

                // Create a new group if the user typed a new name
                if (newGroupName && !groupId) {
                    const res = await createGroupV3({ name: newGroupName, privacy: 'public' });
                    const newGroup = res?.data?.data || res?.data;
                    if (!newGroup?.id) {
                        message.error('Failed to create group');
                        setExecuting(false);
                        return;
                    }
                    groupId = newGroup.id;
                }

                const payload = {
                    processId,
                    contacts: mappedContacts.filter(c => c.isValid),
                    importStrategy,
                    groupId
                };
                
                const res = await apiCore.post('/api/v3/contacts/import/execute', payload);
                const data = res.data || res;
                message.success(`Imported: ${data.created} created, ${data.updated} updated.`);
                
                if (onFinish) onFinish({ ...data, groupId });
                handleCancel(); // This resets state and closes the modal
            } else {
                 if (onFinish) onFinish(mappedContacts);
                 handleCancel();
            }
        } catch (e) {
            console.error(e);
            message.error('Execution failed: ' + (e.response?.data?.message || e.message));
        } finally {
            setExecuting(false);
        }
    };

    // State
    const [previewRowIndex, setPreviewRowIndex] = useState(0);

    const tableContainerRef = useRef(null);
    const [tableScrollY, setTableScrollY] = useState(300);

    useEffect(() => {
        if (currentStep !== 2) return undefined;
        const el = tableContainerRef.current;
        if (!el) return undefined;
        // Table chrome: header (~40) + pagination with its margins (~40)
        const CHROME = 80;
        const update = () => setTableScrollY(Math.max(200, el.clientHeight - CHROME));
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [currentStep]);

    // ... (existing constants)

    // Renderers
    const renderMappingStep = () => {
        const renderColumnSelect = (val, onChange, status, disabled = false) => (
             <Select 
                style={{ width: '100%' }} 
                value={val} 
                onChange={onChange}
                allowClear
                placeholder={disabled ? "Derived from Full Name" : "Select CSV Header"}
                status={status}
                disabled={disabled}
            >
                 {headers.map(h => (
                     <Option key={h} value={h}>{h}</Option>
                 ))}
             </Select>
        );

        // Core Fields Table Logic
        const isFullNameMapped = !!fixedMapping['fullname'];
        
        // Calculate previews for Name fields
        let previewFirst = '-';
        let previewLast = '-';
        const previewRow = csvData[previewRowIndex] || {};
        
        if (isFullNameMapped && csvData.length > 0) {
            const fullVal = previewRow[fixedMapping['fullname']];
            if (fullVal) {
                const parsed = parseFullName(fullVal);
                previewFirst = parsed.first || '-';
                previewLast = [parsed.middle, parsed.last].filter(Boolean).join(' ') || '-';
            }
        } else if (csvData.length > 0) {
             previewFirst = fixedMapping['firstname'] ? (previewRow[fixedMapping['firstname']] || '-') : '-';
             previewLast = fixedMapping['lastname'] ? (previewRow[fixedMapping['lastname']] || '-') : '-';   
        }

        const previewHeader = (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Preview</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Button 
                        type="text" 
                        size="small"
                        icon={<LeftOutlined />}
                        disabled={previewRowIndex === 0}
                        onClick={() => setPreviewRowIndex(p => Math.max(0, p - 1))}
                    />
                    <Text type="secondary" style={{ fontSize: 12, minWidth: 40, textAlign: 'center' }}>
                        {previewRowIndex + 1} / {csvData.length}
                    </Text>
                    <Button 
                        type="text" 
                        size="small"
                        icon={<RightOutlined />}
                        disabled={previewRowIndex >= csvData.length - 1}
                        onClick={() => setPreviewRowIndex(p => Math.min(csvData.length - 1, p + 1))}
                    />
                </div>
            </div>
        );

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                 <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
                     {/* 1. Core Fields Table */}
                     <Card 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Core Fields</span>
                                <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
                                    <InfoCircleOutlined style={{ marginRight: 4 }} /> 
                                    Loaded {csvData.length} rows from CSV
                                </Text>
                            </div>
                        } 
                        size="small" 
                        style={{ marginBottom: 16 }}
                     >
                         <table style={{ width: '100%' }}>
                             <thead>
                                 <tr style={{ textAlign: 'left' }}>
                                     <th style={{ width: '30%', padding: 8 }}>Target Field</th>
                                     <th style={{ width: '40%', padding: 8 }}>Map to CSV Column</th>
                                     <th style={{ width: '30%', padding: 8 }}>{previewHeader}</th>
                                 </tr>
                             </thead>
                             <tbody>
                                 {/* Full Name */}
                                 <tr>
                                     <td style={{ padding: 8 }}>
                                         <Text strong>Full Name</Text>
                                         <div style={{ fontSize: 12, color: '#888' }}>Map this to auto-fill First/Last</div>
                                     </td>
                                     <td style={{ padding: 8 }}>
                                        {renderColumnSelect(
                                            fixedMapping['fullname'],
                                            v => setFixedMapping(m => {
                                                const next = { ...m, fullname: v };
                                                if (v) { delete next.firstname; delete next.lastname; }
                                                return next;
                                            })
                                        )}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                         <Text code>{fixedMapping['fullname'] ? (previewRow[fixedMapping['fullname']] || '-') : '-'}</Text>
                                     </td>
                                 </tr>
                                 
                                 {/* First Name (Sub) */}
                                 <tr>
                                     <td style={{ padding: '8px 8px 8px 24px' }}>
                                         <Text type="secondary" style={{ marginRight: 8 }}>↳</Text> First Name 
                                         {!isFullNameMapped && <Text type="danger" style={{ marginLeft: 4 }}>*</Text>}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                        {isFullNameMapped ? (
                                            <Input 
                                                disabled 
                                                value="Derived from Full Name" 
                                                style={{ color: '#888', fontStyle: 'italic' }} 
                                                prefix={<LinkOutlined style={{ color: '#ccc' }} />} 
                                            />
                                        ) : (
                                            renderColumnSelect(
                                                fixedMapping['firstname'],
                                                v => setFixedMapping(m => { const next = { ...m, firstname: v }; delete next.fullname; return next; }),
                                                !fixedMapping['firstname'] ? 'warning' : ''
                                            )
                                        )}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                         <Text code>{previewFirst}</Text>
                                     </td>
                                 </tr>

                                 {/* Last Name (Sub) */}
                                 <tr>
                                     <td style={{ padding: '8px 8px 8px 24px' }}>
                                         <Text type="secondary" style={{ marginRight: 8 }}>↳</Text> Last Name
                                         {!isFullNameMapped && <Text type="danger" style={{ marginLeft: 4 }}>*</Text>}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                        {isFullNameMapped ? (
                                            <Input 
                                                disabled 
                                                value="Derived from Full Name" 
                                                style={{ color: '#888', fontStyle: 'italic' }} 
                                                prefix={<LinkOutlined style={{ color: '#ccc' }} />} 
                                            />
                                        ) : (
                                            renderColumnSelect(
                                                fixedMapping['lastname'],
                                                v => setFixedMapping(m => { const next = { ...m, lastname: v }; delete next.fullname; return next; }),
                                                !fixedMapping['lastname'] ? 'warning' : ''
                                            )
                                        )}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                         <Text code>{previewLast}</Text>
                                     </td>
                                 </tr>

                                 {/* Mobile (Always Required) */}
                                 <tr>
                                     <td style={{ padding: 8 }}>
                                         <Text strong>Mobile</Text> <Text type="danger">*</Text>
                                     </td>
                                     <td style={{ padding: 8 }}>
                                        {renderColumnSelect(
                                            fixedMapping['mobile'], 
                                            v => setFixedMapping(m => ({ ...m, mobile: v })),
                                            !fixedMapping['mobile'] ? 'warning' : ''
                                        )}
                                        {fixedMapping['mobile'] && (
                                            <div style={{ marginTop: 8 }}>
                                                {hasNakedNumbers && (
                                                    <Alert 
                                                        type="info" 
                                                        showIcon 
                                                        icon={<InfoCircleOutlined />}
                                                        message="Some numbers lack a + country code. Please specify which country they belong to:"
                                                        style={{ marginBottom: 8, padding: '4px 8px' }}
                                                    />
                                                )}
                                                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                                                    {hasNakedNumbers ? "Assume naked numbers are from:" : "Default country for naked numbers:"}
                                                </div>
                                                <Select 
                                                    showSearch
                                                    style={{ width: '100%' }} 
                                                    size="small"
                                                    value={defaultCountry}
                                                    onChange={setDefaultCountry}
                                                    placeholder="Select Country"
                                                    optionFilterProp="label"
                                                    status={hasNakedNumbers ? "warning" : ""}
                                                >
                                                    {Object.keys(COUNTRY_FORMATS).map(iso => {
                                                        let label = iso.toUpperCase();
                                                        try {
                                                            const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
                                                            label = `${displayNames.of(iso.toUpperCase())} (${iso.toUpperCase()})`;
                                                        } catch (e) {}
                                                        return { iso: iso.toUpperCase(), label };
                                                    }).sort((a, b) => a.label.localeCompare(b.label)).map(item => (
                                                        <Option key={item.iso} value={item.iso} label={item.label}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <div className={`flag ${item.iso.toLowerCase()}`} />
                                                                {item.label}
                                                            </div>
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </div>
                                        )}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                         {fixedMapping['mobile'] ? (
                                             <PhoneDisplay value={previewRow[fixedMapping['mobile']] || ''} country={defaultCountry} />
                                         ) : '-'}
                                     </td>
                                 </tr>

                                 {/* Email (Optional Core) */}
                                 <tr>
                                     <td style={{ padding: 8 }}>
                                         <Text strong>Email</Text>
                                     </td>
                                     <td style={{ padding: 8 }}>
                                        {renderColumnSelect(
                                            fixedMapping['email'], 
                                            v => setFixedMapping(m => ({ ...m, email: v }))
                                        )}
                                     </td>
                                     <td style={{ padding: 8 }}>
                                         <Text code>{fixedMapping['email'] ? (previewRow[fixedMapping['email']] || '-') : '-'}</Text>
                                     </td>
                                 </tr>
                             </tbody>
                         </table>
                     </Card>

                     {/* 2. Custom Fields Table */}
                     <Card 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Additional Fields</span>
                                <Button size="small" type="dashed" onClick={addCustomRow}>+ Add Field</Button>
                            </div>
                        } 
                        size="small"
                        style={{ marginBottom: 12 }}
                     >
                        {customMappings.length === 0 && <Text type="secondary" style={{ padding: 8 }}>No optional fields mapped. Add one to include extra data from your file.</Text>}
                        {customMappings.length > 0 && (
                             <table style={{ width: '100%' }}>
                                 <thead>
                                     <tr style={{ textAlign: 'left' }}>
                                         <th style={{ width: '30%', padding: 8 }}>Target Field Name</th>
                                         <th style={{ width: '40%', padding: 8 }}>Map to CSV Column</th>
                                         <th style={{ width: '25%', padding: 8 }}>Preview</th>
                                         <th style={{ width: '5%', padding: 8 }}></th>
                                     </tr>
                                 </thead>
                                 <tbody>
                                     {customMappings.map(row => {
                                         const col = row.column;
                                         const val = col ? previewRow[col] : '-';
                                         
                                         return (
                                            <tr key={row.id}>
                                                <td style={{ padding: 8 }}>
                                                    <Input 
                                                        placeholder="e.g. Tags, Department"
                                                        value={row.target}
                                                        onChange={e => {
                                                            const newVal = e.target.value;
                                                            setCustomMappings(customMappings.map(m => m.id === row.id ? { ...m, target: newVal } : m));
                                                        }}
                                                    />
                                                </td>
                                                <td style={{ padding: 8 }}>
                                                    {renderColumnSelect(
                                                        row.column, 
                                                        v => setCustomMappings(customMappings.map(m => m.id === row.id ? { ...m, column: v } : m))
                                                    )}
                                                </td>
                                                <td style={{ padding: 8 }}>
                                                    <Text code>{val && val.length > 30 ? val.substring(0, 30) + '...' : val}</Text>
                                                </td>
                                                <td style={{ padding: 8 }}>
                                                    <Button type="text" danger onClick={() => removeCustomRow(row.id)}>X</Button>
                                                </td>
                                            </tr>
                                         );
                                     })}
                                 </tbody>
                             </table>
                        )}
                     </Card>
                 </div>

                 <div style={{ flexShrink: 0, marginTop: 16, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                    <Button onClick={() => setCurrentStep(0)} style={{ marginRight: 8 }}>Back</Button>
                    <Button type="primary" onClick={handleMapNext} loading={validating}>
                        Next: Validate
                    </Button>
                </div>
            </div>
        );
    };



    const renderReviewStep = () => {
        // Status Column First
        const columns = [
            { 
                title: 'Status', 
                key: 'status', 
                fixed: 'left',
                width: 150,
                render: (_, record) => {
                    if (!record.isValid) {
                        return (
                            <Space direction="vertical" size={0}>
                                {record.validationErrors.map((err, idx) => {
                                    if (err === 'Invalid Mobile') {
                                        return (
                                            <Tooltip key={idx} title="We couldn’t match a country for this number, so it was skipped.">
                                                <span style={{ cursor: 'help' }}>
                                                    <Text type="danger">{err}</Text>
                                                    <InfoCircleOutlined style={{ fontSize: 12, marginLeft: 4, color: '#ff4d4f' }} />
                                                </span>
                                            </Tooltip>
                                        );
                                    }
                                    return <Text key={idx} type="danger">{err}</Text>;
                                })}
                            </Space>
                        );
                    }
                    if (record.isDuplicate) {
                        return (
                            <Tooltip title="Click to compare with existing record">
                                <span style={{ cursor: 'pointer' }}>
                                    <Text type="warning">{record.isArchived ? 'Archived Duplicate' : 'Duplicate'}</Text>
                                    <PicCenterOutlined style={{ fontSize: 12, marginLeft: 4, color: '#faad14' }} />
                                </span>
                            </Tooltip>
                        );
                    }
                    return <Text type="success">Ready</Text>;
                }
            },
            { title: 'First Name', dataIndex: 'firstname', key: 'firstname', width: 150 },
            { title: 'Last Name', dataIndex: 'lastname', key: 'lastname', width: 150 },
            { 
                title: 'Mobile', 
                dataIndex: 'mobile', 
                key: 'mobile', 
                width: 200,
                render: (text, record) => (
                    <div style={{ color: (!record.isValid && !record.isDuplicate) ? 'red' : 'inherit' }}>
                        <PhoneDisplay value={text} country={defaultCountry} />
                    </div>
                )
            }
        ];

        // Email column if mapped
        if (fixedMapping['email'] || customMappings.some(m => m.target.toLowerCase().trim() === 'email')) {
            columns.push({ title: 'Email', dataIndex: 'email', key: 'email', width: 200 });
        }

        // Additional columns from custom mappings (excluding tags, groups, email handled above)
        customMappings.forEach(m => {
            const targetLower = m.target.toLowerCase().trim();
            if (['tags', 'tag', 'groups', 'group', 'email'].includes(targetLower)) return;
            
            // Avoid duplicate columns
            if (columns.some(col => col.key === m.target)) return;

            columns.push({
                title: m.target,
                dataIndex: m.target,
                key: m.target,
                width: 150,
                render: (val) => val || '-'
            });
        });

        const filteredContacts = mappedContacts.filter(c => {
            // 1. Text Search
            if (searchText) {
                const search = searchText.toLowerCase();
                const matchesSearch = (
                    (c.firstname && c.firstname.toLowerCase().includes(search)) ||
                    (c.lastname && c.lastname.toLowerCase().includes(search)) ||
                    (c.mobile && c.mobile.toLowerCase().includes(search))
                );
                if (!matchesSearch) return false;
            }

            // 2. Category Filter
            if (reviewFilter === 'invalid') return !c.isValid && !c.isDuplicate;
            if (reviewFilter === 'duplicate') return c.isDuplicate;
            if (reviewFilter === 'valid') return c.isValid && !c.isDuplicate;

            return true;
        });

        const filterItemStyle = (type) => ({
            cursor: 'pointer',
            padding: '2px 8px',
            borderRadius: 4,
            backgroundColor: reviewFilter === type ? '#f0f0f0' : 'transparent',
            fontWeight: reviewFilter === type ? 'bold' : 'normal',
            transition: 'all 0.2s',
            border: reviewFilter === type ? '1px solid #ddd' : '1px solid transparent'
        });

        const nothingToImport = ((validationSummary?.valid || 0) + (validationSummary?.duplicate || 0)) === 0;
        const missingGroup = requireGroup && !selectedGroupId && !newGroupName;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                 <div style={{ display: 'flex', gap: 24, marginBottom: 16, alignItems: 'center', padding: '0 8px' }}>
                     <div style={{ flex: '0 0 40%' }}>
                        <Input.Search 
                            placeholder="Search by name or mobile" 
                            allowClear 
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: '100%' }}
                        />
                     </div>
                     <div style={{ flex: '1', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {validationSummary && (
                            <Space size="middle" style={{ color: '#666', fontSize: 13 }}>
                                <div 
                                    onClick={() => setReviewFilter('all')} 
                                    style={filterItemStyle('all')}
                                >
                                    Total: <strong>{validationSummary.total}</strong>
                                </div>
                                <div style={{ color: '#ddd' }}>|</div>
                                <div 
                                    onClick={() => setReviewFilter('valid')} 
                                    style={{ ...filterItemStyle('valid'), color: reviewFilter === 'valid' ? '#52c41a' : '#666' }}
                                >
                                    Valid: <strong>{validationSummary.valid}</strong>
                                </div>
                                <div 
                                    onClick={() => setReviewFilter('duplicate')} 
                                    style={{ ...filterItemStyle('duplicate'), color: reviewFilter === 'duplicate' ? '#faad14' : '#666' }}
                                >
                                    Duplicates: <strong>{validationSummary.duplicate}</strong>
                                </div>
                                <div 
                                    onClick={() => setReviewFilter('invalid')} 
                                    style={{ ...filterItemStyle('invalid'), color: reviewFilter === 'invalid' ? '#ff4d4f' : '#666' }}
                                >
                                    Invalids: <strong>{validationSummary.invalid}</strong>
                                </div>
                            </Space>
                        )}
                     </div>
                 </div>

                <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Table
                        className="gh-custom-table"
                        dataSource={filteredContacts} 
                        columns={columns}
                        rowKey={(r, i) => i}
                        expandable={{
                            expandedRowRender: (record) => {
                                const existing = record.existingRecord || {};
                                const fields = [
                                    { label: 'First Name', key: 'firstname' },
                                    { label: 'Last Name', key: 'lastname' },
                                    { label: 'Email', key: 'email' },
                                    { label: 'Mobile', key: 'mobile' }
                                ];

                                return (
                                    <div style={{ margin: '8px 0', padding: 12, backgroundColor: '#fafafa', borderRadius: 4, border: '1px solid #f0f0f0' }}>
                                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                                            <Text strong>Comparison with existing record found in system</Text>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid #e8e8e8', textAlign: 'left' }}>
                                                    <th style={{ padding: '4px 8px', color: '#888', fontWeight: 'normal', width: '20%' }}>Field</th>
                                                    <th style={{ padding: '4px 8px', color: '#888', fontWeight: 'normal', width: '40%' }}>Existing (Gohire)</th>
                                                    <th style={{ padding: '4px 8px', color: '#888', fontWeight: 'normal', width: '40%' }}>New (Importing)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fields.map(f => {
                                                    const existingVal = String(existing[f.key] || '').trim();
                                                    const newVal = String(record[f.key] || '').trim();
                                                    const isDifferent = existingVal !== newVal && newVal !== '';

                                                    return (
                                                        <tr key={f.label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                            <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>{f.label}</td>
                                                            <td style={{ padding: '6px 8px', color: '#666' }}>{existingVal || '-'}</td>
                                                            <td style={{ padding: '6px 8px', backgroundColor: isDifferent ? '#fffbe6' : 'transparent' }}>
                                                                <Text type={isDifferent ? 'warning' : 'secondary'}>
                                                                    {newVal || '-'}
                                                                    {isDifferent && <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 12 }} />}
                                                                </Text>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
                                            <InfoCircleOutlined style={{ marginRight: 4 }} />
                                            Differences are highlighted. Importing will update the existing record based on your strategy.
                                        </div>
                                    </div>
                                );
                            },
                            rowExpandable: (record) => record.isDuplicate,
                            expandIconColumnIndex: 0
                        }}
                        pagination={{ 
                            pageSize: 50, 
                            showSizeChanger: true, 
                            position: ['bottomRight'],
                            style: { marginBottom: 12 }
                        }}
                        scroll={{
                            x: filteredContacts.length > 0 ? 'max-content' : undefined,
                            y: tableScrollY
                        }}
                        size="small"
                    />
                </div>

                <div style={{ flexShrink: 0, marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    {validationSummary?.invalid > 0 && (
                        <div style={{ color: '#ff4d4f', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <ExclamationCircleOutlined />
                            <span>{validationSummary.invalid} records are not valid and are excluded.</span>
                        </div>
                    )}

                    {mode === 'execute' && (
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={targetGroupId ? 24 : 12}>
                                <Text strong>Import Strategy for Duplicates:</Text><br/>
                                <Tooltip title={validationSummary?.duplicate === 0 ? "No duplicates detected" : ""}>
                                    <div>
                                        <Radio.Group
                                            onChange={e => setImportStrategy(e.target.value)}
                                            value={importStrategy}
                                            style={{ marginTop: 8 }}
                                            disabled={validationSummary?.duplicate === 0}
                                        >
                                            <Radio value="skip">{targetGroupId ? 'Add to group (Keep existing record)' : 'Use record already on GoHire'}</Radio>
                                            <Radio value="update">{targetGroupId ? 'Add to group (Update record)' : 'Update record'}</Radio>
                                        </Radio.Group>
                                    </div>
                                </Tooltip>
                            </Col>
                            {!targetGroupId && (
                                <Col span={12}>
                                    <Text strong>Add to Group{requireGroup ? ':' : ' (Optional):'}</Text><br/>
                                    <Select
                                        style={{ width: '100%', marginTop: 8 }}
                                        allowClear
                                        showSearch
                                        placeholder="Select or create a group"
                                        value={selectedGroupId || (newGroupName ? `new:${newGroupName}` : undefined)}
                                        onSearch={setGroupSearchText}
                                        onChange={(val) => {
                                            if (val && String(val).startsWith('new:')) {
                                                const name = String(val).replace('new:', '');
                                                setNewGroupName(name);
                                                setSelectedGroupId(null);
                                            } else {
                                                setSelectedGroupId(val || null);
                                                setNewGroupName('');
                                            }
                                            setGroupSearchText('');
                                        }}
                                        onClear={() => {
                                            setSelectedGroupId(null);
                                            setNewGroupName('');
                                            setGroupSearchText('');
                                        }}
                                        filterOption={(input, option) => {
                                            if (option.value && String(option.value).startsWith('new:')) return true;
                                            return (option.label || '').toLowerCase().includes(input.toLowerCase());
                                        }}
                                    >
                                        {groupSearchText.trim() && !availableGroups
                                            .filter(g => !g.privacy || g.privacy === 'public' || g.user_id === (session.get('userData') || {}).id)
                                            .some(g => g.name.toLowerCase() === groupSearchText.trim().toLowerCase()) && (
                                            <Option key="__create_new__" value={`new:${groupSearchText.trim()}`} label={groupSearchText.trim()}>
                                                <div style={{ display: 'flex', alignItems: 'center', color: '#52c41a' }}>
                                                    <PlusOutlined style={{ marginRight: 8 }} />
                                                    <span>Create &ldquo;{groupSearchText.trim()}&rdquo;</span>
                                                </div>
                                            </Option>
                                        )}
                                        {availableGroups
                                            .filter(g => !g.privacy || g.privacy === 'public' || g.user_id === (session.get('userData') || {}).id)
                                            .map(g => (
                                                <Option key={g.id} value={g.id} label={g.name}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {g.name} ({g.contact_count || 0} contacts)
                                                        </span>
                                                        {g.privacy === 'private' && (
                                                            <Tag icon={<LockOutlined />} style={{ marginLeft: 8, marginRight: 0 }}>Private</Tag>
                                                        )}
                                                    </div>
                                                </Option>
                                            ))}
                                    </Select>
                                </Col>
                            )}
                        </Row>
                    )}
                    
                    <div style={{ textAlign: 'right' }}>
                        <Button onClick={() => setCurrentStep(1)} style={{ marginRight: 8 }}>Back</Button>
                        <Tooltip title={nothingToImport ? "There is nothing valid to import" : missingGroup ? "Please select or create a group" : ""}>
                            {mode === 'execute' ? (
                                <Popconfirm
                                    title="Confirm Import"
                                    description={
                                        <div style={{ marginTop: 8 }}>
                                            <div>
                                                Importing: <Text strong type="success">
                                                    {(validationSummary?.valid || 0) + (validationSummary?.duplicate || 0)}
                                                </Text> contacts
                                                {(validationSummary?.duplicate || 0) > 0 && (
                                                    <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>
                                                        ({validationSummary.duplicate} existing{importStrategy === 'update' ? ' — will update' : ' — as-is'})
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                Skipping: <Text strong type="warning">
                                                    {validationSummary?.invalid || 0}
                                                </Text> invalid contacts
                                            </div>
                                        </div>
                                    }
                                    onConfirm={handleExecute}
                                    okText="Proceed"
                                    cancelText="Cancel"
                                    disabled={nothingToImport || missingGroup}
                                >
                                    <Button
                                        type="primary"
                                        loading={executing}
                                        disabled={nothingToImport || missingGroup}
                                    >
                                        Import Contacts
                                    </Button>
                                </Popconfirm>
                            ) : (
                                <Button
                                    type="primary"
                                    onClick={handleExecute}
                                    loading={executing}
                                    disabled={nothingToImport}
                                >
                                    Add to Staging
                                </Button>
                            )}
                        </Tooltip>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            visible={visible}
            title="Import Contacts"
            centered
            width="80%"
            style={{ maxWidth: 1000, top: 20 }}
            onCancel={handleCancel}
            footer={null}
            destroyOnClose
            maskClosable={false}
        >
            <Steps size="small" current={currentStep} style={{ marginBottom: 24 }}>
                <Step title="Upload" icon={<InboxOutlined />} />
                <Step title="Map Columns" icon={<PicCenterOutlined />} />
                <Step title="Verify & Execute" icon={<ExclamationCircleOutlined />} />
            </Steps>
            <div style={{ display: 'flex', flexDirection: 'column', height: currentStep === 0 ? 'auto' : '70vh', transition: 'height 0.3s ease' }}>
                {currentStep === 0 && (
                    <div style={{ height: 350, marginBottom: 24 }}>
                        <Dragger
                            name="file"
                            accept=".csv,.tsv,.xls,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            showUploadList={false}
                            beforeUpload={handleUpload}
                            style={{ height: '100%', padding: '40px 0' }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                            <p className="ant-upload-hint">
                                Supports CSV, TSV, XLS, and XLSX. Max size 5MB.
                            </p>
                        </Dragger>
                        {uploading && <div style={{ textAlign: 'center', marginTop: 16 }}>Parsing File...</div>}
                    </div>
                )}

                {currentStep === 1 && renderMappingStep()}
                {currentStep === 2 && renderReviewStep()}
            </div>
        </Modal>
    );
};

export default ImportContactsModal;
